import { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { Button } from "../components/Buttons";
import {
  getOfflineOptions,
  submitOfflineCollection,
  listMyOfflineCollections,
} from "../api";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB — matches the server cap

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const STATUS_META = {
  pending: {
    label: "Awaiting approval",
    color: "warning",
    icon: <HourglassTopRoundedIcon sx={{ fontSize: 16 }} />,
  },
  approved: {
    label: "Approved",
    color: "success",
    icon: <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />,
  },
  rejected: {
    label: "Rejected",
    color: "error",
    icon: <CancelRoundedIcon sx={{ fontSize: 16 }} />,
  },
};

/* ================================================================== */
/*  QR CARD                                                            */
/* ================================================================== */

function QrCard({ qr, selected, onSelect, onZoom }) {
  const [copied, setCopied] = useState(false);

  const copyUpi = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(qr.upiId || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card
      onClick={() => onSelect(qr.id)}
      sx={(theme) => ({
        cursor: "pointer",
        borderRadius: 3,
        height: "100%",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.04)
          : "background.paper",
        transition: "border-color .15s, box-shadow .15s, transform .15s",
        "&:hover": {
          borderColor: "primary.light",
          transform: "translateY(-2px)",
          boxShadow: 3,
        },
      })}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
          <Typography fontWeight={700} fontSize={14} noWrap flex={1}>
            {qr.label}
          </Typography>
          {selected && (
            <Chip size="small" color="primary" label="Selected" sx={{ height: 20, fontSize: 10 }} />
          )}
        </Stack>

        <Box
          sx={{
            position: "relative",
            bgcolor: "#fff",
            borderRadius: 2,
            p: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {qr.imageUrl ? (
            <Box
              component="img"
              src={qr.imageUrl}
              alt={qr.label}
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ aspectRatio: "1 / 1" }}
            >
              <QrCode2RoundedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">
                Image unavailable
              </Typography>
            </Stack>
          )}
          {qr.imageUrl && (
            <Tooltip title="View full size">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onZoom(qr);
                }}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
              >
                <ZoomInRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {qr.upiId && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            mt={1.5}
            sx={{
              bgcolor: "action.hover",
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", flex: 1 }}
              noWrap
            >
              {qr.upiId}
            </Typography>
            <Tooltip title={copied ? "Copied" : "Copy UPI ID"}>
              <IconButton size="small" onClick={copyUpi}>
                <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {qr.note && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {qr.note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

/* ================================================================== */
/*  PAGE                                                               */
/* ================================================================== */

export default function PayOffline() {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  const [method, setMethod] = useState("qr");
  const [qrCodeId, setQrCodeId] = useState("");
  const [posMachineId, setPosMachineId] = useState("");
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [zoomQr, setZoomQr] = useState(null);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [viewSlip, setViewSlip] = useState(null);

  useEffect(() => {
    getOfflineOptions()
      .then(({ data }) => {
        setOptions(data);
        /* Default to whichever method actually has something configured. */
        if (!data.qrCodes?.length && data.posMachines?.length) setMethod("pos");
        if (data.qrCodes?.length === 1) setQrCodeId(data.qrCodes[0].id);
        if (data.posMachines?.length === 1)
          setPosMachineId(data.posMachines[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    listMyOfflineCollections(page, 10)
      .then(({ data }) => {
        setRows(data.collections || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [page]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after a remove
    if (!picked) return;
    if (!ACCEPTED.includes(picked.type)) {
      return setFeedback({
        severity: "error",
        msg: "Upload a JPG, PNG, WEBP or PDF",
      });
    }
    if (picked.size > MAX_UPLOAD_SIZE) {
      return setFeedback({ severity: "error", msg: "File must be under 5 MB" });
    }
    const dataUrl = await readFileAsDataUrl(picked);
    setFile({ name: picked.name, type: picked.type, dataUrl });
    setFilePreview(picked.type === "application/pdf" ? null : dataUrl);
    setFeedback(null);
  };

  const resetForm = () => {
    setAmount("");
    setUtr("");
    setRemarks("");
    setFile(null);
    setFilePreview(null);
  };

  const canSubmit =
    !!amount &&
    Number(amount) > 0 &&
    utr.trim().length >= 6 &&
    !!file &&
    (method === "qr" ? !!qrCodeId : true);

  const handleSubmit = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      await submitOfflineCollection({
        method,
        amount: Number(amount),
        utr: utr.trim(),
        screenshotDataUrl: file.dataUrl,
        qrCodeId: method === "qr" ? qrCodeId : undefined,
        posMachineId: method === "pos" ? posMachineId || undefined : undefined,
        remarks: remarks.trim() || undefined,
      });
      resetForm();
      setFeedback({
        severity: "success",
        msg: "Submitted. Your wallet is credited once an admin approves it.",
      });
      setPage(1);
      loadHistory();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not submit the request",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const qrCodes = options?.qrCodes || [];
  const posMachines = options?.posMachines || [];
  const nothingConfigured = !qrCodes.length && !posMachines.length;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        POS & QR Collections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Paid on a POS machine or by scanning a QR? Enter the UTR and attach the
        receipt — your wallet is credited once an admin verifies it, with
        commission shared up your chain as usual.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      {nothingConfigured && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          No QR codes or POS machines have been set up yet. Please contact your
          admin.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ---------------- Left: pick where you paid ---------------- */}
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                1 · How did you pay?
              </Typography>

              <ToggleButtonGroup
                value={method}
                exclusive
                onChange={(_, v) => v && setMethod(v)}
                fullWidth
                sx={{
                  mb: 3,
                  "& .MuiToggleButton-root": {
                    borderRadius: 2,
                    py: 1.25,
                    fontWeight: 600,
                    textTransform: "none",
                  },
                }}
              >
                <ToggleButton value="qr" disabled={!qrCodes.length}>
                  <QrCode2RoundedIcon sx={{ mr: 1, fontSize: 20 }} />
                  Scanned a QR
                </ToggleButton>
                <ToggleButton value="pos" disabled={!posMachines.length}>
                  <PointOfSaleRoundedIcon sx={{ mr: 1, fontSize: 20 }} />
                  POS machine
                </ToggleButton>
              </ToggleButtonGroup>

              {method === "qr" ? (
                qrCodes.length ? (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={1.5}>
                      Scan one of these to pay, then pick the same one below.
                    </Typography>
                    <Grid container spacing={2}>
                      {qrCodes.map((qr) => (
                        <Grid item xs={6} sm={4} key={qr.id}>
                          <QrCard
                            qr={qr}
                            selected={qrCodeId === qr.id}
                            onSelect={setQrCodeId}
                            onZoom={setZoomQr}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </>
                ) : (
                  <Alert severity="info">No QR codes are available yet.</Alert>
                )
              ) : posMachines.length ? (
                <>
                  <Typography variant="body2" color="text.secondary" mb={1.5}>
                    {options?.machinesAssigned
                      ? "These are the machines assigned to your account."
                      : "No machine is assigned to you yet — pick the one you used."}
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Which machine did you pay on?"
                    value={posMachineId}
                    onChange={(e) => setPosMachineId(e.target.value)}
                  >
                    {posMachines.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.label}
                        {m.terminalId ? ` · TID ${m.terminalId}` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                  {posMachines.find((m) => m.id === posMachineId)?.note && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={1}
                    >
                      {posMachines.find((m) => m.id === posMachineId).note}
                    </Typography>
                  )}
                </>
              ) : (
                <Alert severity="info">No POS machines are available yet.</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ---------------- Right: the claim form ---------------- */}
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                2 · Payment details
              </Typography>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Amount paid"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  label="UTR / Reference number"
                  value={utr}
                  onChange={(e) => setUtr(e.target.value.toUpperCase())}
                  placeholder="e.g. 428391047726"
                  helperText="From your UPI app or the POS slip"
                  inputProps={{ style: { fontFamily: "monospace" } }}
                />

                {/* Screenshot */}
                <Box>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    Payment screenshot / slip
                  </Typography>

                  {file ? (
                    <Box
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <ReceiptLongRoundedIcon
                          sx={{ color: "success.main", fontSize: 20 }}
                        />
                        <Typography variant="body2" noWrap flex={1}>
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFile(null);
                            setFilePreview(null);
                          }}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                      {filePreview && (
                        <Box
                          component="img"
                          src={filePreview}
                          alt="Payment proof"
                          sx={{
                            mt: 1,
                            width: "100%",
                            maxHeight: 200,
                            objectFit: "contain",
                            borderRadius: 1.5,
                            bgcolor: "action.hover",
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Button
                      component="label"
                      variant="outlined"
                      fullWidth
                      startIcon={<UploadFileRoundedIcon />}
                      sx={{
                        /* height is fixed at 44px by the shared Button —
                           release it so this reads as a drop zone. */
                        height: "auto",
                        py: 2.5,
                        borderRadius: 2,
                        borderStyle: "dashed",
                        textTransform: "none",
                        fontSize: 15,
                      }}
                    >
                      Upload screenshot or slip
                      <input
                        hidden
                        type="file"
                        accept={ACCEPTED.join(",")}
                        onChange={handleFile}
                      />
                    </Button>
                  )}
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Remarks (optional)"
                  multiline
                  minRows={2}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  loading={submitting}
                  disabled={!canSubmit || submitting}
                  onClick={handleSubmit}
                  sx={{ borderRadius: 2, py: 1.25 }}
                >
                  Submit for approval
                </Button>

                <Typography variant="caption" color="text.secondary">
                  Nothing is credited until an admin verifies the payment
                  against the merchant statement.
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ---------------- History ---------------- */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 4, mb: 1.5 }}>
        My requests
      </Typography>
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        {historyLoading ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : rows.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 5 }}>
            <ReceiptLongRoundedIcon
              sx={{ fontSize: 44, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">
              You haven't submitted any POS or QR payments yet
            </Typography>
          </Stack>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Paid to</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>UTR</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Slip</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const corrected =
                    r.status === "approved" &&
                    r.approvedAmount !== null &&
                    Number(r.approvedAmount) !== Number(r.amount);
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(r.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={
                            r.method === "pos" ? (
                              <PointOfSaleRoundedIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <QrCode2RoundedIcon sx={{ fontSize: 14 }} />
                            )
                          }
                          label={r.method === "pos" ? "POS" : "QR"}
                          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {r.qrLabel || r.machineLabel || "—"}
                        </Typography>
                        {r.machineTerminalId && (
                          <Typography variant="caption" color="text.secondary">
                            TID {r.machineTerminalId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {corrected ? (
                          <Tooltip
                            title={`You entered ${INR(r.amount)} — admin corrected it`}
                          >
                            <span>
                              <Typography
                                component="span"
                                sx={{
                                  textDecoration: "line-through",
                                  color: "text.disabled",
                                  fontSize: 12,
                                  mr: 0.5,
                                }}
                              >
                                {INR(r.amount)}
                              </Typography>
                              {INR(r.approvedAmount)}
                            </span>
                          </Tooltip>
                        ) : (
                          INR(r.approvedAmount ?? r.amount)
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {r.utr}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={r.rejectionReason || ""}>
                          <Chip
                            size="small"
                            color={meta.color}
                            icon={meta.icon}
                            label={meta.label}
                            sx={{ height: 24, fontSize: 11, fontWeight: 600 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {r.screenshotUrl ? (
                          <IconButton
                            size="small"
                            onClick={() => setViewSlip(r)}
                          >
                            <ZoomInRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      {total > 10 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={Math.ceil(total / 10)}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Stack>
      )}

      {/* ---------------- QR zoom ---------------- */}
      <Dialog
        open={!!zoomQr}
        onClose={() => setZoomQr(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight={700}>{zoomQr?.label}</DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 3 }}>
          {zoomQr?.imageUrl && (
            <Box
              component="img"
              src={zoomQr.imageUrl}
              alt={zoomQr.label}
              sx={{ width: "100%", borderRadius: 2, bgcolor: "#fff" }}
            />
          )}
          {zoomQr?.upiId && (
            <Typography
              sx={{ fontFamily: "monospace", mt: 2, fontWeight: 600 }}
            >
              {zoomQr.upiId}
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------------- Slip viewer ---------------- */}
      <Dialog
        open={!!viewSlip}
        onClose={() => setViewSlip(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle fontWeight={700}>Payment proof</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {viewSlip && (
            <>
              <Stack direction="row" gap={2} flexWrap="wrap" mb={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography fontWeight={700}>
                    {INR(viewSlip.approvedAmount ?? viewSlip.amount)}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    UTR
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace" }}>
                    {viewSlip.utr}
                  </Typography>
                </Box>
              </Stack>
              {viewSlip.rejectionReason && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {viewSlip.rejectionReason}
                </Alert>
              )}
              <Box
                component="img"
                src={viewSlip.screenshotUrl}
                alt="Payment proof"
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
